import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { PORTAL_COTACOES_HABILITADO } from '../api/portalCotacoes/portalCotacoesApi';
import { conectarPortalSocket, desconectarPortalSocket } from '../api/portalCotacoes/socket';
import useUserType from './useUserType';
import { papeisModuloNovo } from '../shared/modulos-novos';

/**
 * Papéis que enxergam o Portal — mesma lista do `RoleGuard` das rotas no `App.tsx` e do
 * `@Roles` das rotas de leitura do backend. Quem não está aqui não tem o módulo no menu e
 * não deve abrir conexão nenhuma.
 */
const PAPEIS_DO_MODULO = ['COMPRA', 'ADMIN', 'TI'];

/**
 * Conexão websocket do Portal de Cotações (ADR 0005) — montada UMA vez, no `App.tsx`.
 *
 * O que ela entrega: o sino, o badge do menu e a fila do gestor deixam de esperar o ciclo
 * de 30 segundos. O polling **continua existindo** como rede de segurança — socket é
 * aceleração, nunca dependência: conexão caída é a experiência anterior, não tela quebrada.
 *
 * ⚠️ **Gate triplo**, e os três importam:
 *  - **env do módulo**: sem ela a URL seria `undefined` e o módulo nem existe na UI;
 *  - **token**: sem sessão não há o que autenticar no handshake;
 *  - **papel**: sem isto, todo usuário logado abriria uma conexão com um backend que ele
 *    não usa — é a mesma lição do at-67 (BUG-V38) que vale para o badge do menu, só que
 *    aqui o custo é uma conexão viva, não uma requisição a cada 30 segundos.
 *
 * O evento é republicado como `CustomEvent` porque nem toda tela do Portal usa react-query:
 * a fila do gestor e o Sidebar guardam estado próprio e precisam de um gancho para reagir.
 */
export function usePortalCotacoesSocket() {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const userType = useUserType();

  useEffect(() => {
    if (!PORTAL_COTACOES_HABILITADO) return;
    if (!accessToken) return;
    // papeisModuloNovo (at-74): em teste, só o ADMIN abre a conexão.
    if (!userType || !papeisModuloNovo(PAPEIS_DO_MODULO).includes(userType)) return;

    const socket = conectarPortalSocket(accessToken);
    if (!socket) return;

    /*
     * ⚠️ O `CustomEvent` é o ÚNICO canal, e isso é deliberado: nenhuma tela do Portal usa
     * react-query para os avisos — o sino, o Sidebar e a fila do gestor guardam estado
     * próprio. Uma versão anterior deste hook invalidava `queryKeys.portalCotacoes
     * .notificacoes` e não acordava ninguém, porque essa query não existe em componente
     * nenhum (o sino nasceu com react-query e passou a estado local ao virar dropdown).
     * O sentinela do sino é o que pega essa classe de engano.
     */
    const aoReceberAviso = (detail: { tipo: string; cotacaoId: string | null }) => {
      window.dispatchEvent(new CustomEvent('portal:notificacao', { detail }));
    };

    socket.on('notificacao:criada', aoReceberAviso);

    return () => {
      socket.off('notificacao:criada', aoReceberAviso);
      desconectarPortalSocket();
    };
  }, [accessToken, userType]);
}
